import React, { useState, useEffect } from 'react'
import Icon from './IconLibrary'
import { useAuth } from '../context/AuthContext.jsx'

export default function MemoryGame() {
  const { user, tokens } = useAuth()
  const [cards, setCards] = useState([])
  const [flipped, setFlipped] = useState([])
  const [matched, setMatched] = useState([])
  const [gameComplete, setGameComplete] = useState(false)
  const [celebrating, setCelebrating] = useState([])
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [gridSize, setGridSize] = useState('2x2') // '2x2', '3x3', '4x4'
  const [categories, setCategories] = useState([])
  const [selectedLetters, setSelectedLetters] = useState([])
  const [tempSelectedLetters, setTempSelectedLetters] = useState([]) // Temporary selection in modal
  const [loading, setLoading] = useState(true)
  const [adminView, setAdminView] = useState('settings') // 'settings' or 'admin'
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [categoryItems, setCategoryItems] = useState([])
  const [newLetter, setNewLetter] = useState('')
  const [newItem, setNewItem] = useState({ type: 'image', name: '' })

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories()
  }, [])

  // Initialize game when settings change
  useEffect(() => {
    if (categories.length > 0) {
      initializeGame()
    }
  }, [gridSize, selectedLetters, categories])

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories from /api/game/categories')
      const res = await fetch('/api/game/categories')
      console.log('Response status:', res.status)
      const data = await res.json()
      console.log('Categories data:', data)
      
      if (data.success && data.data.categories.length > 0) {
        setCategories(data.data.categories)
        // Auto-select all available letters by default on first load
        if (selectedLetters.length === 0) {
          const letters = data.data.categories.map(c => c.letter)
          console.log('Auto-selecting all letters on first load:', letters)
          setSelectedLetters(letters)
        }
      } else {
        // No categories available
        console.log('No categories found')
        setCategories([])
        setSelectedLetters([])
      }
    } catch (e) {
      console.error('Failed to fetch categories:', e)
    } finally {
      setLoading(false)
    }
  }

  const initializeGame = async () => {
    if (selectedLetters.length === 0) {
      console.log('No letters selected, skipping game init')
      return
    }

    try {
      // Calculate how many cards we need
      const cardsNeeded = gridSize === '2x2' ? 4 : (gridSize === '3x3' ? 8 : 16)
      
      console.log(`Fetching ${cardsNeeded} items for letters:`, selectedLetters)
      
      // Fetch items from API
      const url = `/api/game/items?letters=${selectedLetters.join(',')}&limit=${cardsNeeded}`
      console.log('Fetching from:', url)
      const res = await fetch(url)
      const data = await res.json()
      
      console.log('Game items response:', data)
      
      if (!data.success) {
        console.error('Failed to load items:', data.message)
        alert(data.message || 'Failed to load game items')
        return
      }

      if (!data.data.items || data.data.items.length === 0) {
        alert('No items found for selected letters. Please add items via Game Admin.')
        return
      }

      // Transform API data to game cards
      const gameCards = data.data.items.map(item => {
        console.log(`Card: ${item.name} from letter ${item.letter}`)
        return {
          id: item.id,
          type: item.type,
          content: item.name,
          letter: item.letter, // Store the letter for debugging
          image: `/Images/${item.letter}/${item.name}.png`,
          audio: `/Audio/${item.letter}/${item.name}.mp3`
        }
      })

      console.log('Game cards created:', gameCards)
      console.log('Letters in game:', [...new Set(gameCards.map(c => c.letter))])

      // Shuffle cards
      const shuffled = [...gameCards].sort(() => Math.random() - 0.5)
      
      // For 3x3, insert empty spot in the middle (index 4)
      if (gridSize === '3x3') {
        shuffled.splice(4, 0, { id: 'empty', type: 'empty' })
      }
      
      setCards(shuffled)
      setFlipped([])
      setMatched([])
      setGameComplete(false)
      setCelebrating([])
    } catch (e) {
      console.error('Failed to initialize game:', e)
      alert('Failed to load game. Please try again.')
    }
  }

  const playAudio = (audioPath) => {
    const audio = new Audio(audioPath)
    audio.play().catch(e => console.error('Audio play failed:', e))
  }

  const handleCardClick = (index) => {
    // Don't flip if it's an empty card, already flipped, matched, or two cards are already flipped
    if (cards[index]?.type === 'empty' || flipped.includes(index) || matched.includes(index) || flipped.length === 2) {
      return
    }

    const newFlipped = [...flipped, index]
    setFlipped(newFlipped)

    // Play audio for the card
    playAudio(cards[index].audio)

    // Check for match when two cards are flipped
    if (newFlipped.length === 2) {
      const [first, second] = newFlipped
      const firstCard = cards[first]
      const secondCard = cards[second]

      // Check if cards match (same content)
      if (firstCard.content === secondCard.content && first !== second) {
        // Match found!
        // Check if this is the final match
        // For 3x3, we have 1 empty card, so total playable cards = cards.length - 1
        const totalPlayableCards = gridSize === '3x3' ? cards.length - 1 : cards.length
        const isGameComplete = matched.length + 2 === totalPlayableCards
        
        // Play appropriate sound
        if (isGameComplete) {
          playAudio('/Audio/victory.mp3')
        } else {
          playAudio('/Audio/match.mp3')
        }
        
        // Trigger celebration animation
        setCelebrating([first, second])
        
        setTimeout(() => {
          setMatched([...matched, first, second])
          setFlipped([])
          setCelebrating([])
          
          // Check if game is complete
          if (isGameComplete) {
            setGameComplete(true)
          }
        }, 800)
      } else {
        // No match, flip back after delay
        // Play error sound
        playAudio('/Audio/error.mp3')
        
        setTimeout(() => {
          setFlipped([])
        }, 1200)
      }
    }
  }

  const resetGame = () => {
    initializeGame()
  }

  const fetchCategoryItems = async (categoryId) => {
    try {
      const res = await fetch(`/api/admin/game/categories/${categoryId}/items/`, {
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setCategoryItems(data.data.items)
        setSelectedCategory(data.data.category)
      }
    } catch (e) {
      console.error('Failed to fetch items:', e)
    }
  }

  const createCategory = async (e) => {
    e.preventDefault()
    try {
      const res = await fetch('/api/admin/game/categories/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify({ letter: newLetter.toUpperCase() })
      })
      const data = await res.json()
      if (data.success) {
        setNewLetter('')
        fetchCategories()
      } else {
        alert(data.message)
      }
    } catch (e) {
      console.error('Failed to create category:', e)
      alert('Failed to create category')
    }
  }

  const createItem = async (e) => {
    e.preventDefault()
    if (!selectedCategory) return

    try {
      // Create the first card
      const res1 = await fetch('/api/admin/game/items/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify({
          categoryId: selectedCategory.id,
          type: newItem.type,
          name: newItem.name.trim()
        })
      })
      const data1 = await res1.json()
      
      if (!data1.success) {
        alert(data1.message)
        return
      }

      // Automatically create the second card (matching pair)
      const res2 = await fetch('/api/admin/game/items/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.accessToken}`
        },
        body: JSON.stringify({
          categoryId: selectedCategory.id,
          type: newItem.type,
          name: newItem.name.trim()
        })
      })
      const data2 = await res2.json()
      
      if (data2.success) {
        setNewItem({ type: 'image', name: '' })
        fetchCategoryItems(selectedCategory.id)
        fetchCategories() // Refresh category counts
        alert(`Added pair: 2 cards named "${newItem.name.trim()}"`)
      } else {
        alert('Created first card but failed to create matching pair')
        fetchCategoryItems(selectedCategory.id)
      }
    } catch (e) {
      console.error('Failed to create item:', e)
      alert('Failed to create item')
    }
  }

  const deleteItem = async (itemId) => {
    if (!confirm('Delete this item?')) return

    try {
      const res = await fetch(`/api/admin/game/items/${itemId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        fetchCategoryItems(selectedCategory.id)
        fetchCategories() // Refresh category counts
      }
    } catch (e) {
      console.error('Failed to delete item:', e)
    }
  }

  const deleteCategory = async (categoryId) => {
    if (!confirm('Delete this category and ALL its items? This cannot be undone.')) return

    try {
      const res = await fetch(`/api/admin/game/categories/${categoryId}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tokens?.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setSelectedCategory(null)
        setCategoryItems([])
        fetchCategories()
      }
    } catch (e) {
      console.error('Failed to delete category:', e)
    }
  }

  const isMobile = window.innerWidth < 768
  const isSmallMobile = window.innerWidth <= 360

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <p>Loading game...</p>
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: '0 20px' }}>
        <div>
          <h2 style={{ fontSize: 24, marginBottom: 16 }}>No Game Content Available</h2>
          <p style={{ color: 'var(--muted)', marginBottom: 20 }}>
            Please add letters and items via the Game Admin page.
          </p>
          {user?.role === 'superadmin' && (
            <a href="/game-admin" style={{ 
              display: 'inline-block',
              padding: '10px 20px',
              background: 'var(--accent)',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: 8,
              fontWeight: 600
            }}>
              Go to Game Admin
            </a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ 
      maxWidth: isSmallMobile ? '100%' : 800, 
      margin: '0 auto', 
      padding: isSmallMobile ? '20px 8px' : (isMobile ? '24px 16px' : '40px 20px'),
      textAlign: 'center'
    }}>
      {/* Header with Settings */}
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <h1 style={{ 
          fontSize: isSmallMobile ? 20 : (isMobile ? 24 : 32), 
          fontWeight: 600,
          lineHeight: 1.2,
          margin: 0
        }}>
          Memory Game {selectedLetters.length === 1 ? `— Letter ${selectedLetters[0]}` : `— ${selectedLetters.length} Letters`}
        </h1>
        <button
          onClick={() => {
            // Start with empty selection for intuitive UX
            setTempSelectedLetters([])
            setSettingsOpen(true)
          }}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: isSmallMobile ? 6 : 8,
            background: 'var(--bg)',
            border: '1px solid var(--elev)',
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--fg)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 8%)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bg)'
          }}
          aria-label="Settings"
        >
          <Icon name="gear" size={isSmallMobile ? 18 : 20} />
        </button>
      </div>

     

      {/* Try Again Button */}
      <button
        onClick={resetGame}
        style={{
          padding: isSmallMobile ? '7px 18px' : (isMobile ? '8px 20px' : '10px 24px'),
          background: 'var(--bg)',
          border: '1px solid var(--elev)',
          borderRadius: 20,
          fontSize: isSmallMobile ? 13 : (isMobile ? 14 : 15),
          fontWeight: 500,
          cursor: 'pointer',
          marginBottom: isSmallMobile ? 20 : (isMobile ? 24 : 32),
          transition: 'all 0.15s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 8%)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--bg)'
        }}
      >
        Try again
      </button>

      {/* Game Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: gridSize === '2x2' ? 'repeat(2, 1fr)' : (gridSize === '3x3' ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)'),
        gap: isSmallMobile ? (gridSize === '4x4' ? 4 : 6) : (isMobile ? (gridSize === '4x4' ? 6 : 10) : 16),
        maxWidth: '100%',
        width: '100%',
        margin: '0 auto',
        marginBottom: isSmallMobile ? 20 : (isMobile ? 24 : 32)
      }}>
        {cards.map((card, index) => {
          const isFlipped = flipped.includes(index) || matched.includes(index)
          const isMatched = matched.includes(index)
          const isCelebrating = celebrating.includes(index)

          // Render empty card for 3x3 middle spot
          if (card.type === 'empty') {
            return <div key={index} style={{ aspectRatio: '1' }} />
          }

          return (
            <div
              key={index}
              onClick={() => handleCardClick(index)}
              style={{
                aspectRatio: '1',
                borderRadius: isSmallMobile ? (gridSize === '4x4' ? 6 : 8) : (isMobile ? (gridSize === '4x4' ? 8 : 10) : 12),
                cursor: isMatched ? 'default' : 'pointer',
                position: 'relative',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s',
                transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(0deg)',
                animation: isCelebrating ? 'celebrate 0.6s ease-in-out' : 'none'
              }}
            >
              {/* Card Back (Orange with Letter) */}
              {!isFlipped && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                  borderRadius: isSmallMobile ? (gridSize === '4x4' ? 6 : 8) : (isMobile ? (gridSize === '4x4' ? 8 : 10) : 12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isSmallMobile ? (gridSize === '4x4' ? 24 : (gridSize === '3x3' ? 32 : 40)) : (isMobile ? (gridSize === '4x4' ? 32 : 48) : 64),
                  fontWeight: 700,
                  color: '#1f2937',
                  userSelect: 'none'
                }}>
                  ?
                </div>
              )}

              {/* Card Front (White with Image/Letter) */}
              {isFlipped && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#fff',
                  borderRadius: isSmallMobile ? (gridSize === '4x4' ? 6 : 8) : (isMobile ? (gridSize === '4x4' ? 8 : 10) : 12),
                  border: isMatched ? `${isSmallMobile ? 2 : (isMobile ? 3 : 4)}px solid #16a34a` : `${isSmallMobile ? 2 : (isMobile ? 3 : 4)}px solid transparent`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 2,
                  transition: 'border-color 0.3s'
                }}>
                  {card.type === 'letter' ? (
                    <div style={{
                      background: '#06b6d4',
                      borderRadius: isSmallMobile ? (gridSize === '4x4' ? 3 : 6) : (isMobile ? (gridSize === '4x4' ? 6 : 10) : 16),
                      padding: isSmallMobile ? (gridSize === '4x4' ? '3px 6px' : '6px 12px') : (isMobile ? (gridSize === '4x4' ? '8px 16px' : '12px 24px') : '20px 40px'),
                      fontSize: isSmallMobile ? (gridSize === '4x4' ? 20 : 28) : (isMobile ? (gridSize === '4x4' ? 28 : 40) : 80),
                      fontWeight: 700,
                      color: '#1f2937'
                    }}>
                      {card.content}
                    </div>
                  ) : (
                    <img 
                      src={card.image} 
                      alt={card.content}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: isSmallMobile ? (gridSize === '4x4' ? 4 : 6) : (isMobile ? (gridSize === '4x4' ? 6 : 8) : 8)
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Success Message with Confetti */}
      {gameComplete && (
        <>
          {/* Confetti Container */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: 100
          }}>
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: `${Math.random() * 100}%`,
                  width: '10px',
                  height: '10px',
                  background: ['#f59e0b', '#16a34a', '#06b6d4', '#8b5cf6', '#ec4899'][Math.floor(Math.random() * 5)],
                  borderRadius: Math.random() > 0.5 ? '50%' : '0',
                  animation: `confettiFall ${2 + Math.random() * 3}s linear forwards`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              />
            ))}
          </div>

          <div style={{
            marginTop: isSmallMobile ? 20 : (isMobile ? 24 : 32),
            animation: 'fadeIn 0.5s',
            position: 'relative',
            zIndex: 101
          }}>
            <p style={{ 
              fontSize: isSmallMobile ? 18 : (isMobile ? 20 : 24), 
              fontWeight: 600, 
              color: '#16a34a',
              marginBottom: 16,
              padding: '0 16px',
              lineHeight: 1.3
            }}>
              Great job! You found all the pairs!
            </p>
            <button
              onClick={resetGame}
              style={{
                padding: isSmallMobile ? '9px 24px' : (isMobile ? '10px 28px' : '12px 32px'),
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 24,
                fontSize: isSmallMobile ? 14 : (isMobile ? 15 : 16),
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'color-mix(in oklab, var(--accent), black 10%)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--accent)'
              }}
            >
              Play again
            </button>
          </div>
        </>
      )}

      {/* Settings Modal */}
      {settingsOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div 
            onClick={() => setSettingsOpen(false)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)'
            }}
          />
          <div style={{
            position: 'relative',
            background: 'var(--bg)',
            borderRadius: 16,
            padding: 24,
            maxWidth: adminView === 'admin' ? 700 : 400,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid var(--elev)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.4)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <h3 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
                  {adminView === 'settings' ? 'Game Settings' : 'Manage Content'}
                </h3>
                {user?.role === 'superadmin' && (
                  <button
                    onClick={() => {
                      setAdminView(adminView === 'settings' ? 'admin' : 'settings')
                      setSelectedCategory(null)
                      setCategoryItems([])
                    }}
                    style={{
                      padding: '6px 10px',
                      background: 'color-mix(in oklab, var(--bg), var(--fg) 8%)',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 13,
                      color: 'var(--fg)'
                    }}
                  >
                    <Icon name="document.badge.plus" size={16} />
                    {adminView === 'settings' ? 'Add Content' : 'Back'}
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setSettingsOpen(false)
                  setAdminView('settings')
                  setSelectedCategory(null)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  color: 'var(--fg)'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6L18 18M18 6L6 18"/>
                </svg>
              </button>
            </div>

            {/* Settings View */}
            {adminView === 'settings' && (
              <>
            {/* Grid Size - Only 2x2 available */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 12, display: 'block' }}>
                Grid Size
              </label>
              <div style={{ display: 'grid', gap: 8 }}>
                {['2x2'].map(size => (
                  <button
                    key={size}
                    onClick={() => setGridSize(size)}
                    style={{
                      padding: '12px 16px',
                      background: gridSize === size ? 'var(--accent)' : 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                      color: gridSize === size ? '#fff' : 'var(--fg)',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (gridSize !== size) {
                        e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 10%)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (gridSize !== size) {
                        e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 6%)'
                      }
                    }}
                  >
                    <span>{size} Grid</span>
                    {gridSize === size && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <label style={{ fontSize: 14, fontWeight: 500 }}>
                  Select Letters ({tempSelectedLetters.length} selected)
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setTempSelectedLetters(categories.map(c => c.letter))}
                    style={{
                      padding: '4px 8px',
                      background: 'none',
                      border: '1px solid var(--elev)',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                      color: 'var(--fg)'
                    }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setTempSelectedLetters([])}
                    style={{
                      padding: '4px 8px',
                      background: 'none',
                      border: '1px solid var(--elev)',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                      color: 'var(--fg)'
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                Click letters to select them for the game
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(45px, 1fr))',
                gap: 8,
                maxHeight: 200,
                overflowY: 'auto',
                padding: 8,
                background: 'color-mix(in oklab, var(--bg), var(--fg) 3%)',
                borderRadius: 8
              }}>
                {categories.map(cat => {
                  const isSelected = tempSelectedLetters.includes(cat.letter)
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        if (isSelected) {
                          setTempSelectedLetters(tempSelectedLetters.filter(l => l !== cat.letter))
                        } else {
                          setTempSelectedLetters([...tempSelectedLetters, cat.letter])
                        }
                      }}
                      style={{
                        padding: '10px',
                        background: isSelected ? 'var(--accent)' : 'color-mix(in oklab, var(--bg), var(--fg) 8%)',
                        color: isSelected ? '#fff' : 'var(--fg)',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 16,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      {cat.letter}
                    </button>
                  )
                })}
              </div>
              {tempSelectedLetters.length === 0 && (
                <p style={{ fontSize: 12, color: '#dc2626', margin: '8px 0 0 0' }}>
                  Please select at least one letter
                </p>
              )}
            </div>

            {/* Apply Button */}
            <button
              onClick={() => {
                if (tempSelectedLetters.length > 0) {
                  console.log('Applying settings with letters:', tempSelectedLetters)
                  setSelectedLetters(tempSelectedLetters)
                  setSettingsOpen(false)
                  setAdminView('settings')
                  // Game will reinitialize via useEffect when selectedLetters changes
                }
              }}
              disabled={tempSelectedLetters.length === 0}
              style={{
                width: '100%',
                padding: '12px',
                background: tempSelectedLetters.length > 0 ? 'var(--accent)' : '#999',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 600,
                cursor: tempSelectedLetters.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              Apply Settings
            </button>
            </>
            )}

            {/* Admin View */}
            {adminView === 'admin' && user?.role === 'superadmin' && (
              <div style={{ display: 'grid', gridTemplateColumns: selectedCategory ? '200px 1fr' : '1fr', gap: 20 }}>
                {/* Categories List */}
                <div>
                  <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Letters</h4>
                  
                  {/* Create Category */}
                  <form onSubmit={createCategory} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="text"
                        value={newLetter}
                        onChange={(e) => setNewLetter(e.target.value)}
                        placeholder="A-Z"
                        maxLength={1}
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          border: '1px solid var(--elev)',
                          borderRadius: 6,
                          background: 'var(--bg)',
                          color: 'var(--fg)',
                          fontSize: 14
                        }}
                        required
                      />
                      <button
                        type="submit"
                        style={{
                          padding: '6px 12px',
                          background: 'var(--accent)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 500
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </form>

                  {/* Category List */}
                  <div style={{ display: 'grid', gap: 6, maxHeight: 400, overflowY: 'auto' }}>
                    {categories.map(cat => (
                      <div
                        key={cat.id}
                        onClick={() => fetchCategoryItems(cat.id)}
                        style={{
                          padding: '10px',
                          background: selectedCategory?.id === cat.id ? 'color-mix(in oklab, var(--bg), var(--fg) 12%)' : 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{cat.letter}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{cat.itemCount} items</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items Panel */}
                {selectedCategory && (
                  <div>
                    <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                      Letter {selectedCategory.letter} Items
                    </h4>

                    {/* Add Item Form */}
                    <form onSubmit={createItem} style={{ marginBottom: 16, padding: 12, background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', borderRadius: 8 }}>
                      <div style={{ display: 'grid', gap: 10 }}>
                        <select
                          value={newItem.type}
                          onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                          style={{
                            padding: '8px',
                            border: '1px solid var(--elev)',
                            borderRadius: 6,
                            background: 'var(--bg)',
                            color: 'var(--fg)',
                            fontSize: 14
                          }}
                        >
                          <option value="letter">Letter Card</option>
                          <option value="image">Image Card</option>
                        </select>
                        <input
                          type="text"
                          value={newItem.name}
                          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                          placeholder="Name (e.g., ant, apple)"
                          style={{
                            padding: '8px',
                            border: '1px solid var(--elev)',
                            borderRadius: 6,
                            background: 'var(--bg)',
                            color: 'var(--fg)',
                            fontSize: 14
                          }}
                          required
                        />
                        <button
                          type="submit"
                          style={{
                            padding: '8px',
                            background: 'var(--accent)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 14
                          }}
                        >
                          Add Pair
                        </button>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>
                        Creates 2 matching cards. Files: /Images/{selectedCategory.letter}/{newItem.name || '...'}.png
                      </div>
                    </form>

                    {/* Items List */}
                    <div style={{ display: 'grid', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
                      {categoryItems.map(item => (
                        <div
                          key={item.id}
                          style={{
                            padding: '10px',
                            background: 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                            borderRadius: 6,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: 13
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{item.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Type: {item.type}</div>
                          </div>
                          <button
                            onClick={() => deleteItem(item.id)}
                            style={{
                              padding: '4px 8px',
                              background: '#dc2626',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              fontSize: 11,
                              cursor: 'pointer'
                            }}
                          >
                            Del
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
